import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock, Pencil, Siren } from 'lucide-react';
import { SAMPLE_STATUS_LABEL, SAMPLE_TYPE_LABEL } from '@dru/types';
import type { CursorPage, LabBattery, LabBatteryTest, LabResultCounts, LabWorklistCounts, SampleRow } from '@dru/types';
import { LabStatusStrip } from '../components/LabStatusStrip';
import {
  BenchBackLink, BenchScanBox, classifyScan, scanRefusal, useCheckInHref, useUnsavedGuard, useVenueParam,
} from '../components/BenchNav';
import { useLabLinks } from '../lib/context';
import { flagWord } from '@dru/types';
import {
  Alert, Button, Card, Dialog, IconAction, LoadMore, EmptyState, ErrorState, InfoTip, Input, RowActions, StatusBadge, TextField, TextSkeleton, cn, Select, useToast,
} from '@dru/ui';
import { useAuth } from '../../../lib/auth';
import { holds, PageHeader } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { parseMeasured } from '../../../lib/measured';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { errorMessage } from '../../../lib/auth';
import { eitherOf, useSigners } from '../../../lib/labSignoff';
import { useVenueFilter } from '../../hospital/usePartner';
import { sampleTone, subjectLine } from './Worklist';
import { flagTone } from '../../hospital/routes/Encounter';
import { bandText, readableValue } from './Release';
import { SignatureReadiness } from '../components/SignatureReadiness';

/**
 * Result entry, one subject at a time.
 *
 * The bench used to open one tube, enter its values one "Enter" at a time,
 * then go back for the next tube of the same person. The work is a person's
 * battery, so that is the unit here: every test the subject owes, across all
 * their tubes, in one form with one "Enter all" — each value still sent
 * against the tube it was measured from, so custody is real. A single row can
 * still be entered on its own.
 *
 * The subject is a pseudonym with an age and a sex, which is exactly what the
 * reference band needs and no more.
 *
 * Entry does not release. What is typed here is not evidence until a second
 * person validates and releases it, and the technician who entered it can
 * never be that person — so the screen names who that is, or says plainly
 * that this venue has nobody who can.
 */

/** A tube is at the bench, and its values can be entered. */
const AT_BENCH = new Set(['RECEIVED', 'PROCESSING', 'RESULTED', 'VALIDATED']);
const ARRIVING = new Set(['ORDERED', 'COLLECTED', 'IN_TRANSIT']);
const GONE = new Set(['REJECTED', 'RECOLLECT_REQUESTED']);

interface Subject {
  encounterId: string;
  subject: SampleRow['subject'];
  tubes: SampleRow[];
  sentBack: number;
  toEnter: number;
  arriving: number;
}

export function Results() {
  const venueFilter = useVenueFilter();
  const { query, partnerId } = venueFilter;
  const venueId = useVenueParam(venueFilter);
  const { permissions } = useAuth();
  const checkInHref = useCheckInHref(permissions);
  // Typed, unsaved values in the open subject: leaving asks first.
  const [dirty, setDirty] = useState(0);
  const guard = useUnsavedGuard(dirty);
  // The configured clinical profiles (CBC, LFT, KFT …), so a battery reads
  // as those panels rather than one long list. Read-only catalogue; when it
  // cannot be read, the battery is shown ungrouped.
  const catalogue = useQuery<{ profiles: Array<{ code: string; name: string; kind: string; isActive: boolean; tests: Array<{ code: string }> }> } | null>(
    () => (holds(permissions, 'test.read') ? api.get('/report-profiles') : Promise.resolve(null)), []);
  const profiles = useMemo(() => {
    if (!catalogue.data) return null;
    const m = new Map<string, ProfileInfo>();
    catalogue.data.profiles.filter((p) => p.kind === 'LAB' && p.isActive).forEach((p, order) => {
      for (const t of p.tests) if (!m.has(t.code)) m.set(t.code, { code: p.code, name: p.name, order });
    });
    return m;
  }, [catalogue.data]);
  const [params, setParams] = useSearchParams();
  // Entry is the technician's. A pathologist or lab admin who reaches this
  // screen by its address reads the battery; they are not handed inputs the
  // API would refuse.
  const canEnter = holds(permissions, 'lab.result-enter');
  const signs = holds(permissions, ['lab.result-validate', 'lab.result-release']);
  const signers = useSigners(partnerId || null, canEnter && !signs);

  const samples = usePages<SampleRow>(
    (cursor) => api.get<CursorPage<SampleRow>>(`/lab/worklist?limit=50${query}${cursor ? `&cursor=${cursor}` : ''}`),
    [query],
  );

  // One entry per person: their tubes together. A value sent back is the
  // first thing the bench has to redo, so those subjects lead.
  const subjects = useMemo(() => {
    const by = new Map<string, Subject>();
    for (const t of samples.items) {
      const s = by.get(t.encounterId) ?? {
        encounterId: t.encounterId, subject: t.subject, tubes: [], sentBack: 0, toEnter: 0, arriving: 0,
      };
      s.tubes.push(t);
      s.sentBack += t.sentBack ?? 0;
      if (t.status === 'RECEIVED' || t.status === 'PROCESSING') s.toEnter++;
      if (ARRIVING.has(t.status)) s.arriving++;
      by.set(t.encounterId, s);
    }
    return [...by.values()];
  }, [samples.items]);
  const open = subjects.filter((s) => s.sentBack > 0 || s.toEnter > 0)
    .sort((a, b) => Number(b.sentBack > 0) - Number(a.sentBack > 0));
  const entered = subjects.filter((s) => !open.includes(s) && s.tubes.some((t) => AT_BENCH.has(t.status) || t.status === 'RELEASED'));
  const waiting = subjects.filter((s) => !open.includes(s) && !entered.includes(s) && s.arriving > 0);

  // `?sample=` is how the Samples screen links a tube here; it opens the
  // tube's subject. `?subject=` is this screen's own.
  const sampleParam = params.get('sample') ?? '';
  const asked = params.get('subject')
    ?? samples.items.find((t) => t.id === sampleParam)?.encounterId ?? '';

  // One subject at a time, from a queue: what is owed first, then the tubes
  // still on their way, then what is with the pathologist.
  const queues = { open, waiting, entered } as const;
  // Exact subject counts for the tabs, over every tube in scope; the lists
  // themselves fill a page at a time.
  const subjectCounts = useQuery<LabWorklistCounts>(() => api.get(`/lab/worklist/counts?x=1${query}`), [query, samples.items.length]);
  const resultCounts = useQuery<LabResultCounts>(() => api.get(`/lab/results/counts?x=1${query}`), [query, samples.items.length]);
  const tabCount = (k: 'open' | 'waiting' | 'entered') => {
    const sc = subjectCounts.data?.subjects;
    return sc ? (k === 'open' ? sc.toEnter : k === 'waiting' ? sc.notReceived : sc.entered) : queues[k].length;
  };
  type QueueKey = keyof typeof queues;
  const inQueue = (id: string): QueueKey | null =>
    open.some((s) => s.encounterId === id) ? 'open'
      : waiting.some((s) => s.encounterId === id) ? 'waiting'
        : entered.some((s) => s.encounterId === id) ? 'entered' : null;
  const [queueKey, setQueueKey] = useState<QueueKey>('open');
  const queueOf = (asked && inQueue(asked)) || queueKey;
  const queue = queues[queueOf];
  const index = Math.max(0, queue.findIndex((s) => s.encounterId === asked));
  const selected = queue[index] ?? null;
  const toast = useToast();

  const choose = (encounterId: string) => {
    setDirty(0);
    setParams((p) => { const n = new URLSearchParams(p); n.delete('sample'); n.set('subject', encounterId); return n; }, { replace: true });
    window.scrollTo({ top: 0 });
  };
  const switchQueue = (k: QueueKey) => {
    setQueueKey(k);
    setParams((p) => { const n = new URLSearchParams(p); n.delete('sample'); n.delete('subject'); return n; }, { replace: true });
  };
  const step = (d: -1 | 1) => { const next = queue[index + d]; if (next) choose(next.encounterId); };
  const switchGuarded = (k: QueueKey) => guard(() => switchQueue(k))();
  const stepGuarded = (d: -1 | 1) => guard(() => step(d))();

  // Saving the last value of a subject moves on to the next one.
  const finished = (doneId: string) => {
    const at = open.findIndex((s) => s.encounterId === doneId);
    const next = open[at + 1] ?? open.find((s) => s.encounterId !== doneId) ?? null;
    if (next) {
      choose(next.encounterId);
      toast.toast('success', `${open[at]?.subject.displayName ?? 'Subject'} saved`, `Now ${next.subject.displayName}.`);
    } else {
      toast.toast('success', 'All subjects entered', 'Nothing more is owed at the bench.');
    }
  };

  // A scanned tube barcode or a typed subject code opens that subject.
  const [scan, setScan] = useState('');
  const [scanMiss, setScanMiss] = useState<string | null>(null);
  const find = () => {
    // A card or a pass is named for what it is, never searched as a tube.
    const refusal = scanRefusal(classifyScan(scan));
    if (refusal) { setScanMiss(refusal); return; }
    const code = scan.trim().toLowerCase().replace(/^subject\s+/, '');
    if (!code) return;
    const hit = subjects.find((s) => s.tubes.some((t) => t.barcode.toLowerCase() === code)
      || s.subject.displayName.toLowerCase().replace(/^subject\s+/, '') === code);
    if (!hit) { setScanMiss(`Nothing here matches “${scan.trim()}”.`); return; }
    if (dirty && !window.confirm(`You have ${dirty} typed value${dirty === 1 ? '' : 's'} not saved. Discard ${dirty === 1 ? 'it' : 'them'}?`)) return;
    setScanMiss(null); setScan('');
    const k = inQueue(hit.encounterId);
    if (k) setQueueKey(k);
    choose(hit.encounterId);
  };

  const who = signers.data?.signers.filter((x) => !x.isYou).map((x) => x.fullName) ?? [];
  const nobodySigns = canEnter && !signs && !!signers.data?.partnerId && who.length === 0;

  return (
    <div className="space-y-4">
      <BenchBackLink className="-mb-2" />
      <PageHeader
        title={canEnter ? 'Result entry' : 'Results by subject'}
        hint={canEnter
          ? 'Each subject’s tests across all their tubes, in one form. Values are flagged against the subject’s own age and sex band, and a critical value is escalated the moment it is entered. Entered values go to a pathologist to validate and release.'
          : 'What each subject’s tubes owe and what has been entered, read-only. Entering values is a laboratory technician’s task.'}
      />

      {!canEnter && (
        <p className="flex items-center gap-1.5 text-small text-neutral-600">
          <Lock aria-hidden className="h-4 w-4 text-neutral-400" />
          Read-only — results are entered by a Laboratory technician.
        </p>
      )}

      {canEnter && <SignatureReadiness act="enter" />}

      {nobodySigns && (
        <Alert tone="warning" title="This venue has no Pathologist login">
          Entered values wait for a Pathologist to validate and release them. Next: your venue admin adds one.
        </Alert>
      )}

      <LabStatusStrip tubes={subjectCounts.data?.tubes} results={resultCounts.data} canSign={signs} />

      {samples.error && <ErrorState title="Could not load samples" detail={samples.error} onRetry={samples.reload} />}

      {/* The queue bar: where you are, the way on, and a scanner box to jump. */}
      <Card className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3">
        <div role="tablist" aria-label="Which subjects" className="inline-flex max-w-full gap-0.5 overflow-x-auto rounded-btn bg-neutral-100 p-0.5 max-md:w-full">
          {([['open', 'To enter'], ['waiting', 'Not received'], ['entered', 'Entered']] as const).map(([k, label]) => {
            const on = queueOf === k;
            const n = tabCount(k);
            return (
              <button key={k} type="button" role="tab" aria-selected={on} onClick={() => switchGuarded(k)}
                      className={cn(
                        'inline-flex min-h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[calc(theme(borderRadius.btn)-2px)] px-3 text-small transition-colors duration-hover max-md:min-h-10',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                        on ? 'bg-white font-medium text-neutral-900 shadow-card' : 'text-neutral-600 hover:text-neutral-900',
                      )}>
                {label}
                <span className={cn('text-caption tabular-nums', k === 'open' && n > 0 ? 'font-semibold text-primary-700' : 'text-neutral-500')}>{n}</span>
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-center gap-2 max-md:w-full max-md:basis-full max-md:justify-between">
          <Button variant="secondary" size="sm" disabled={index <= 0} onClick={() => stepGuarded(-1)}
                  icon={<ChevronLeft aria-hidden className="h-4 w-4" />} aria-label="Previous subject">
            <span className="max-sm:hidden">Previous</span>
          </Button>
          <span className="min-w-[7.5rem] text-center text-small text-neutral-700" aria-live="polite">
            {queue.length ? <><b className="tabular-nums text-neutral-900">{index + 1}</b> of <span className="tabular-nums">{queue.length}</span></> : 'None'}
          </span>
          <Button variant="secondary" size="sm" disabled={index >= queue.length - 1} onClick={() => stepGuarded(1)} aria-label="Next subject">
            <span className="max-sm:hidden">Next</span><ChevronRight aria-hidden className="h-4 w-4" />
          </Button>
        </div>

        <div className="w-full sm:w-64">
          <BenchScanBox value={scan} onChange={(v) => { setScan(v); setScanMiss(null); }} onSubmit={find}
                        placeholder="Scan tube or subject code" label="Scan a tube barcode or type a subject code" />
          {scanMiss && (
            <p role="alert" className="mt-1 text-caption text-danger-600">
              {scanMiss}
              {checkInHref && /ID card|appointment pass/.test(scanMiss) && (
                <> <Link to={checkInHref} className="font-medium underline">Open the check-in desk</Link></>
              )}
            </p>
          )}
        </div>
      </Card>

      {samples.loading && !samples.items.length ? <TextSkeleton lines={6} />
        : selected
          ? <SubjectBattery key={selected.encounterId} subject={selected} permissions={permissions} canEnter={canEnter}
              signers={who} signersKnown={!!signers.data?.partnerId} onChanged={samples.reload}
              hasNext={queueOf === 'open' && open.length > 1} onFinished={() => finished(selected.encounterId)}
              onDirty={setDirty} profiles={profiles} venueId={venueId} />
          : (
            <EmptyState
              title={queueOf === 'open' ? 'Nothing to enter' : queueOf === 'waiting' ? 'No tubes on their way' : 'Nothing entered yet'}
              hint={queueOf === 'open'
                ? 'A subject appears here once their tubes are received at the bench. Receive tubes on Samples, or scan one above.'
                : queueOf === 'waiting' ? 'Every drawn tube has reached the bench.' : 'Entered subjects appear here once their values are saved.'}
            />
          )}
      <LoadMore hasMore={samples.hasMore} loading={samples.loadingMore} onLoadMore={samples.loadMore} shown={samples.items.length} />
    </div>
  );
}

/** Critical first, then anything out of range, so the eye lands on what matters. */
const SEVERITY: Record<string, number> = { CRITICAL_HIGH: 0, CRITICAL_LOW: 0, HIGH: 1, LOW: 1, ABNORMAL: 1, NORMAL: 2 };
const bySeverity = (a: LabBatteryTest, b: LabBatteryTest) =>
  (SEVERITY[a.result?.flag ?? 'NORMAL'] ?? 2) - (SEVERITY[b.result?.flag ?? 'NORMAL'] ?? 2);
const isCritical = (flag: string | undefined) => flag === 'CRITICAL_HIGH' || flag === 'CRITICAL_LOW';

/** The body a value is sent as, or the reason it cannot be. */
function entryBody(test: LabBatteryTest, raw: string): { ok: true; body: Record<string, unknown> } | { ok: false; message: string } {
  // Which field carries the value is decided by the same configuration that
  // decided which input was rendered. The server re-checks it against the
  // TestDefinition regardless — this is the convenient path, not the guarantee.
  const body: Record<string, unknown> = { testId: test.id, ...(test.sample ? { sampleId: test.sample.id } : {}) };
  if (test.valueType === 'NUMERIC') {
    // "abc" used to leave here as null and be stored as 0 — a haemoglobin of
    // 0 g/dL, escalated as a panic value. Refused at the box instead.
    const m = parseMeasured(raw, { unit: test.unit, plausible: test.plausibleRange });
    if (!m.ok) return { ok: false, message: m.message };
    body.valueNumeric = m.value;
  }
  else if (test.valueType === 'BOOLEAN') body.valueBoolean = raw === 'true';
  else if (test.valueType === 'ENUM') body.valueOptionCode = raw;
  else body.valueText = raw;
  return { ok: true, body };
}

/**
 * What the server will flag a typed number as, shown while it is typed — so
 * 140 typed for 14.0 reads "Critical high" before it is saved, not after.
 * A preview from the same band the server uses; the server's flag is final.
 */
type Preview = 'NORMAL' | 'LOW' | 'HIGH' | 'CRITICAL_LOW' | 'CRITICAL_HIGH';
function previewFlag(t: LabBatteryTest, raw: string): Preview | null {
  if (t.valueType !== 'NUMERIC' || raw.trim() === '') return null;
  const m = parseMeasured(raw, { unit: t.unit, plausible: t.plausibleRange });
  if (!m.ok) return null;
  const r = t.referenceRange; const v = m.value;
  if (!r) return null;
  if (r.criticalLow != null && v < r.criticalLow) return 'CRITICAL_LOW';
  if (r.criticalHigh != null && v > r.criticalHigh) return 'CRITICAL_HIGH';
  if (r.low != null && v < r.low) return 'LOW';
  if (r.high != null && v > r.high) return 'HIGH';
  return 'NORMAL';
}
/** Ten times the top of the range, or a tenth of the bottom: usually a slipped decimal point. */
function looksLikeDecimalSlip(t: LabBatteryTest, raw: string): boolean {
  const r = t.referenceRange; const v = Number(raw);
  if (!r || !Number.isFinite(v) || v === 0) return false;
  return (r.high != null && r.high > 0 && v >= r.high * 10) || (r.low != null && r.low > 0 && v <= r.low / 10);
}

interface Pending {
  t: LabBatteryTest; raw: string; body: Record<string, unknown>; flag: Preview | null;
  /** Minted once when the value is checked, and reused on every retry: a value
   *  the server already saved is replayed, never written twice. */
  idempotencyKey: string;
}

interface ProfileInfo { code: string; name: string; order: number }

/**
 * The tests in the order the configured clinical profiles give them: CBC,
 * then Liver, Kidney … each under its own heading, and anything no profile
 * holds under "Standalone tests". With no catalogue, one ungrouped list.
 */
function byProfile(tests: LabBatteryTest[], profiles: Map<string, ProfileInfo> | null) {
  if (!profiles) return [{ key: 'all', title: null as string | null, tests }];
  const groups = new Map<string, { key: string; title: string | null; order: number; tests: LabBatteryTest[] }>();
  for (const t of tests) {
    const p = profiles.get(t.code);
    const key = p?.code ?? '~other';
    const g = groups.get(key) ?? { key, title: p?.name ?? 'Standalone tests', order: p?.order ?? 9999, tests: [] };
    g.tests.push(t);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => a.order - b.order);
}

function SubjectBattery({ subject, permissions, canEnter, signers, signersKnown, onChanged, hasNext, onFinished, onDirty, profiles, venueId }: {
  /** How many typed values are not yet saved. */
  onDirty?: (n: number) => void;
  profiles: Map<string, ProfileInfo> | null;
  venueId: string;
  /** Another subject waits after this one. */
  hasNext?: boolean;
  /** Every owed value of this subject is saved: the queue moves on. */
  onFinished?: () => void;
  subject: Subject; permissions: ReadonlySet<string>; canEnter: boolean;
  /** Who else at the venue validates; empty with `signersKnown` means nobody. */
  signers: string[]; signersKnown: boolean;
  onChanged: () => void;
}) {
  // The whole battery; each test names the tube it is entered against.
  const battery = useQuery<LabBattery>(
    () => api.get(`/lab/encounters/${subject.encounterId}/battery`), [subject.encounterId]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'all' | 'receive' | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // Values checked and waiting for the reviewer's confirmation.
  const [review, setReview] = useState<Pending[] | null>(null);
  // What did not go through on the last submit, kept with its original keys.
  const [failedItems, setFailedItems] = useState<Pending[]>([]);
  const canReceive = holds(permissions, 'lab.sample-accession');
  const signs = holds(permissions, ['lab.result-validate', 'lab.result-release']);
  const links = useLabLinks();
  const typed = Object.values(values).filter((v) => v !== '').length;
  useEffect(() => { onDirty?.(typed); }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (battery.loading && !battery.data) return <TextSkeleton lines={6} />;
  if (battery.error || !battery.data) {
    return <ErrorState title="Could not load this battery" detail={battery.error ?? undefined} onRetry={battery.reload} />;
  }

  const b = battery.data;
  const reload = () => { battery.reload(); onChanged(); };
  // A test can be entered once its tube is at the bench; a test with no
  // specimen configured has no tube to wait for.
  const atBench = (t: LabBatteryTest) => !t.sample || AT_BENCH.has(t.sample.status);
  const tubeOrder = (t: LabBatteryTest) => t.sample?.barcode ?? '~';
  const openTests = b.tests.filter((t) => !t.result)
    // What the pathologist sent back first: those are the ones to redo.
    .sort((a, c) => Number(!!c.sentBack) - Number(!!a.sentBack) || tubeOrder(a).localeCompare(tubeOrder(c)));
  const waitingTests = b.tests.filter((t) => t.result && !t.result.releasedAt).sort(bySeverity);
  const released = b.tests.filter((t) => t.result?.releasedAt).sort(bySeverity);
  const critical = b.tests.filter((t) => t.result && isCritical(t.result.flag)).length;
  const done = b.tests.length - openTests.length;
  const requiredOpen = openTests.filter((t) => t.isMandatory).length;
  const sentBackOpen = openTests.filter((t) => t.sentBack).length;
  const tubes = subject.tubes.filter((t) => !GONE.has(t.status));
  const arriving = tubes.filter((t) => ARRIVING.has(t.status) && t.status !== 'ORDERED');
  const filled = b.tests.filter((t) => (values[t.id] ?? '') !== '' && !t.result?.releasedAt && atBench(t));

  const setValue = (id: string, v: string) => {
    setValues((m) => ({ ...m, [id]: v }));
    if (errors[id]) setErrors((m) => { const n = { ...m }; delete n[id]; return n; });
  };

  async function send(t: LabBatteryTest, body: Record<string, unknown>, idempotencyKey: string) {
    // The idempotency key belongs to the value, not the click: a double-click
    // or a retry replays the stored response rather than writing a second
    // attempt against the same test.
    await api.post(`/lab/encounters/${b.encounterId}/results`, body, idempotencyKey);
    setValues((m) => { const n = { ...m }; delete n[t.id]; return n; });
  }

  /** Reads every value first; nothing is sent until the summary is confirmed. */
  function check(list: LabBatteryTest[]) {
    const ready: Pending[] = [];
    const bad: Record<string, string> = {};
    for (const t of list) {
      const raw = values[t.id] ?? '';
      if (raw === '') continue;
      const r = entryBody(t, raw);
      if (r.ok) ready.push({ t, raw, body: r.body, flag: previewFlag(t, raw), idempotencyKey: crypto.randomUUID() }); else bad[t.id] = r.message;
    }
    if (Object.keys(bad).length) { setErrors((m) => ({ ...m, ...bad })); return; }
    if (ready.length) setReview(ready);
  }

  /** Each confirmed value against its own tube. */
  async function sendAll(items: Pending[]) {
    setBusy('all'); setFailure(null); setBanner(null);
    let saved = 0;
    const failed: Pending[] = [];
    for (const p of items) {
      try { await send(p.t, p.body, p.idempotencyKey); saved++; }
      catch (e) { failed.push(p); setErrors((m) => ({ ...m, [p.t.id]: errorMessage(e) })); }
    }
    setBusy(null); setReview(null); setFailedItems(failed);
    const allDone = saved === items.length && openTests.every((t) => items.some((p) => p.t.id === t.id));
    if (allDone && onFinished) { reload(); onFinished(); return; }
    // The next state, said: entered values wait for an independent review.
    setBanner(!failed.length
      ? `${saved} value${saved === 1 ? '' : 's'} submitted for review — next, a pathologist validates and releases ${saved === 1 ? 'it' : 'them'}.`
      : `${saved} of ${items.length} submitted for review; ${failed.length} not — see the rows marked below.`);
    reload();
  }

  /** Enter in a box moves to the next empty box; saving is one step at the foot. */
  function focusNext(t: LabBatteryTest) {
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[id^="lab-"]'));
    const me = inputs.findIndex((el) => el.id === `lab-${t.id}`);
    const next = inputs.slice(me + 1).find((el) => !el.disabled && el.value === '');
    if (next) next.focus(); else document.getElementById('lab-check-all')?.focus();
  }

  async function receiveAll() {
    setBusy('receive'); setFailure(null);
    try {
      const r = await api.post<{ received: string[] }>(`/lab/encounters/${b.encounterId}/samples/receive`, {}, crypto.randomUUID());
      setBanner(`${r.received.length} tube${r.received.length === 1 ? '' : 's'} received at the bench.`);
      reload();
    } catch (e) { setFailure(errorMessage(e)); } finally { setBusy(null); }
  }

  // Who the entered values wait on, by name where the venue has someone.
  const next = openTests.length
    ? canEnter
      ? `Enter ${openTests.length} result${openTests.length === 1 ? '' : 's'}${requiredOpen ? ` (${requiredOpen} required)` : ''}`
      : `A technician enters ${openTests.length} result${openTests.length === 1 ? '' : 's'}`
    : waitingTests.length
      ? signers.length
        ? `${eitherOf(signers)} validates and releases ${waitingTests.length} result${waitingTests.length === 1 ? '' : 's'}`
        : signersKnown && !signs
          ? `${waitingTests.length} result${waitingTests.length === 1 ? '' : 's'} wait for a Pathologist — this venue has none`
          : `A pathologist validates and releases ${waitingTests.length} result${waitingTests.length === 1 ? '' : 's'}`
      : released.length ? 'Nothing — every result is released' : 'Nothing is owed to the bench';

  // Checked as the box is left, not only on Enter: "abc" or a value outside
  // the plausible range is named at the row while the printout is in hand.
  const checkOne = (t: LabBatteryTest) => {
    const raw = values[t.id] ?? '';
    if (raw === '' || t.valueType !== 'NUMERIC') return;
    const r = entryBody(t, raw);
    if (!r.ok) setErrors((m) => ({ ...m, [t.id]: r.message }));
  };

  const rowProps = (t: LabBatteryTest) => ({
    test: t, canEnter, value: values[t.id] ?? '', onChange: (v: string) => setValue(t.id, v), onBlur: () => checkOne(t),
    error: errors[t.id] ?? null, onEnter: async () => check([t]), onNext: () => focusNext(t),
    preview: previewFlag(t, values[t.id] ?? ''), slip: t.valueType === 'NUMERIC' && looksLikeDecimalSlip(t, values[t.id] ?? ''),
    disabled: busy !== null, atBench: atBench(t), onAmended: reload,
  });

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-caption text-neutral-500">Now entering</div>
            <div className="font-mono text-h4 text-neutral-900">{b.subject.displayName}</div>
            <div className="text-small text-neutral-600">
              {subjectLine(b.subject) || 'Sex and age not recorded'}
              {' '}<InfoTip label="About reference ranges">Reference ranges are chosen for this band.</InfoTip>
            </div>
          </div>
          <div className="min-w-[10rem] text-right">
            <div className="text-caption text-neutral-500">Entered</div>
            <div className="text-h4 tabular-nums text-neutral-900">{done} of {b.tests.length}</div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100" aria-hidden>
              <div className="h-full bg-primary-600" style={{ width: `${b.tests.length ? (done / b.tests.length) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
        <ul className="flex flex-wrap gap-2 px-4 py-3" aria-label="Tubes">
          {tubes.map((t) => (
            <li key={t.id} className="flex items-center gap-2 rounded-input border border-neutral-200 px-2.5 py-1.5">
              <span className="font-mono text-caption text-neutral-900">{t.barcode}</span>
              <span className="text-caption text-neutral-500">{SAMPLE_TYPE_LABEL[t.sampleType] ?? t.sampleType}</span>
              {(t.sentBack ?? 0) > 0
                ? <StatusBadge tone="warning">Sent back — re-enter</StatusBadge>
                : <StatusBadge tone={sampleTone(t.status)}>{SAMPLE_STATUS_LABEL[t.status] ?? t.status}</StatusBadge>}
            </li>
          ))}
        </ul>
        {arriving.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-4 py-3">
            <span className="text-small text-neutral-700">
              {arriving.length} tube{arriving.length === 1 ? ' is' : 's are'} drawn but not received at the bench yet. Their tests open once received.
            </span>
            {canReceive && (
              <Button size="sm" variant="secondary" loading={busy === 'receive'} disabled={busy !== null} onClick={() => void receiveAll()}>
                Receive {arriving.length === 1 ? 'the tube' : `all ${arriving.length} tubes`}
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-neutral-200 px-4 py-3 text-small">
          <span>
            <span className="text-neutral-500">Next: </span>
            <span className="font-medium text-neutral-900">{next}</span>
          </span>
          {!openTests.length && waitingTests.length > 0 && signs && (
            <Link to={links.to('release', { subject: b.encounterId, venue: venueId, back: true })} className="font-medium text-primary-700 hover:underline">
              Open Validate &amp; release
            </Link>
          )}
        </div>
      </Card>

      {banner && (
        <div role="status" className={cn('flex flex-wrap items-center gap-2 rounded-card border px-3 py-2 text-small',
          failedItems.length ? 'border-warning-600/40 bg-warning-100 text-warning-600' : 'border-covered-600/25 bg-covered-100 text-covered-600')}>
          <span className="min-w-0 flex-1">{banner}</span>
          {!failedItems.length && signs && (
            <Link to={links.to('release', { subject: b.encounterId, venue: venueId, back: true })} className="font-medium underline">
              Open Validate &amp; release
            </Link>
          )}
          {failedItems.length > 0 && (
            // Only what failed, each with the key it was first sent with.
            <Button size="sm" variant="secondary" loading={busy === 'all'} onClick={() => void sendAll(failedItems)}>
              Retry failed ({failedItems.length})
            </Button>
          )}
        </div>
      )}
      {failure && <Alert tone="danger">{failure}</Alert>}

      {critical > 0 && (
        <Alert tone="danger">
          <Siren aria-hidden className="mr-1.5 inline h-4 w-4 align-text-bottom" />
          {critical} critical value{critical === 1 ? '' : 's'} in this battery — escalated when entered, and marked in red below.
        </Alert>
      )}

      {!b.tests.length && (
        <Alert tone="info">No pathology test is owed for this subject. Nothing is due at the bench.</Alert>
      )}

      {openTests.length > 0 && (
        <Card>
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-200 px-4 py-2.5">
            <h3 className="text-body font-semibold text-neutral-900">
              {canEnter ? 'To enter' : 'Not entered yet'} <span className="font-normal text-neutral-500">· {openTests.length}</span>
            </h3>
            {sentBackOpen > 0 && (
              <span className="text-caption font-medium text-warning-600">{sentBackOpen} sent back — listed first</span>
            )}
          </div>
          <ProfileRows tests={openTests} profiles={profiles} row={rowProps} />
          {canEnter && (
            // Held at the foot of a phone's screen, as the station's is.
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 bg-neutral-50 px-4 py-3 max-md:sticky max-md:bottom-0 max-md:z-10">
              <span className="text-caption text-neutral-600">
                {filled.length
                  ? `${filled.length} value${filled.length === 1 ? '' : 's'} typed. You will see them all once more before they are saved.`
                  : 'Type each value from the analyser printout. Enter moves to the next box.'}
              </span>
              <Button id="lab-check-all" loading={busy === 'all'} disabled={!filled.length || busy !== null}
                      onClick={() => check(filled.filter((t) => !t.result))}>
                {filled.length
                  ? `Check and enter ${filled.length}${hasNext && filled.filter((t) => !t.result).length >= openTests.length ? ' → next' : ''}`
                  : 'Check and enter'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {review && (
        <ReviewDialog items={review} subject={b.subject.displayName} busy={busy === 'all'}
                      onBack={() => setReview(null)} onConfirm={() => void sendAll(review)} />
      )}

      <TestGroup title="Entered — awaiting the pathologist" meta={waitingTests.length && canEnter ? 'Re-entering writes a new attempt' : undefined}
        tests={waitingTests} row={rowProps} profiles={profiles} />
      <TestGroup title="Released" meta={released.length ? 'Final — changed only by an amendment' : undefined}
        tests={released} row={rowProps} profiles={profiles} />
    </div>
  );
}

/** The rows of one list, under their profile headings. */
function ProfileRows({ tests, profiles, row }: {
  tests: LabBatteryTest[]; profiles: Map<string, ProfileInfo> | null;
  row: (t: LabBatteryTest) => Parameters<typeof EntryRow>[0];
}) {
  const groups = byProfile(tests, profiles);
  return (
    <div className="divide-y divide-neutral-200">
      {groups.map((g) => (
        <section key={g.key} aria-label={g.title ?? undefined}>
          {g.title && (
            <h4 className="flex items-baseline justify-between bg-neutral-50 px-4 py-1.5 text-caption font-semibold text-neutral-700">
              {g.title}<span className="font-normal tabular-nums text-neutral-500">{g.tests.length}</span>
            </h4>
          )}
          <div className="divide-y divide-neutral-200">
            {g.tests.map((t) => <EntryRow key={t.id} {...row(t)} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function TestGroup({ title, meta, tests, row, profiles }: {
  title: string; meta?: string; tests: LabBatteryTest[];
  row: (t: LabBatteryTest) => Parameters<typeof EntryRow>[0];
  profiles: Map<string, ProfileInfo> | null;
}) {
  if (!tests.length) return null;
  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-neutral-200 px-4 py-2.5">
        <h3 className="text-body font-semibold text-neutral-900">{title} <span className="font-normal text-neutral-500">· {tests.length}</span></h3>
        {meta && <span className="text-caption font-normal text-neutral-500">{meta}</span>}
      </div>
      <ProfileRows tests={tests} profiles={profiles} row={row} />
    </Card>
  );
}

/**
 * How a value is captured, rendered entirely from the Test Master.
 *
 * The bench screen used to be one text input for every test: a BOOLEAN needed
 * the technician to literally type "true", a fixed answer set had no dropdown
 * because the platform could not store one, and the reference band the value
 * was about to be flagged against was never shown at all.
 *
 * Nothing here is per-test. Adding a test, or giving an existing one a fixed
 * answer set, changes what this renders without touching this file — which is
 * the property that makes the Test Master the single source of truth rather
 * than a description of it kept in two places.
 */
function ValueInput({ test, value, onChange, onSubmit, onBlur, onRefused, disabled, invalid }: {
  test: LabBatteryTest;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onBlur?: () => void;
  /** A character a number cannot hold was typed and dropped. */
  onRefused?: () => void;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const id = `lab-${test.id}`;
  const enter = (e: { key: string }) => { if (e.key === 'Enter') onSubmit(); };

  if (test.valueType === 'ENUM') {
    // No free-text fallback, deliberately. Where a fixed clinical answer set
    // exists, prose beside it would make the whole set uncountable.
    if (!test.answerOptions.length) {
      return (
        <span className="text-caption text-danger-600">
          No answers configured — this test cannot be entered.
        </span>
      );
    }
    return (
      <Select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={enter}
        className="w-full sm:w-60"
        aria-label={test.name}
      >
        <option value="">Choose…</option>
        {test.answerOptions.map((o) => (
          <option key={o.code} value={o.code}>{o.display}</option>
        ))}
      </Select>
    );
  }

  if (test.valueType === 'BOOLEAN') {
    return (
      <div className="flex items-center gap-1" role="group" aria-label={test.name}>
        {[['true', 'Yes'], ['false', 'No']].map(([v, label]) => (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v)}
            aria-pressed={value === v}
            className={cn(
              'h-10 rounded-btn border px-4 text-body',
              value === v
                ? 'border-primary-600 bg-primary-50 font-semibold text-primary-700'
                : 'border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50',
            )}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  const numeric = test.valueType === 'NUMERIC';
  // Only what a number can hold: digits, one decimal point, a leading minus
  // where the test can read below zero. Anything else never reaches the box.
  const allowMinus = (test.plausibleRange?.low ?? 0) < 0;
  const keepNumeric = (v: string) => {
    let out = v.replace(allowMinus ? /[^0-9.-]/g : /[^0-9.]/g, '');
    if (allowMinus) out = out.replace(/(?!^)-/g, '');
    const dot = out.indexOf('.');
    if (dot >= 0) out = out.slice(0, dot + 1) + out.slice(dot + 1).replace(/\./g, '');
    return out;
  };
  return (
    <Input
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const v = numeric ? keepNumeric(e.target.value) : e.target.value;
        if (numeric && v !== e.target.value) onRefused?.();
        onChange(v);
      }}
      onKeyDown={enter}
      onBlur={onBlur}
      aria-invalid={invalid || undefined}
      inputMode={numeric ? 'decimal' : 'text'}
      placeholder={numeric ? 'Value' : 'Finding'}
      className={cn(numeric ? 'w-32 text-right tabular-nums' : 'w-full sm:w-60', invalid && 'border-danger-600 focus-visible:ring-danger-600')}
      aria-label={test.name}
    />
  );
}

/** The band this subject's value will be flagged against, as a technician reads it. */
const bandLabel = (r: LabBatteryTest['referenceRange']) => bandText(r);

function EntryRow({ test, canEnter, value, onChange, onBlur, error, onEnter, onNext, preview, slip, disabled, atBench, onAmended }: {
  test: LabBatteryTest;
  canEnter: boolean;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error: string | null;
  onEnter: () => Promise<void>;
  /** Enter in the box: to the next one. */
  onNext: () => void;
  /** The flag this typed number will carry, before it is saved. */
  preview: Preview | null;
  /** Ten times off the range: probably a slipped decimal point. */
  slip: boolean;
  disabled: boolean;
  /** The tube is at the bench; a value cannot be entered against a tube still on its way. */
  atBench: boolean;
  onAmended: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [amending, setAmending] = useState(false);
  const [refused, setRefused] = useState(false);
  useEffect(() => { if (!refused) return; const t = setTimeout(() => setRefused(false), 2500); return () => clearTimeout(t); }, [refused]);
  const { permissions } = useAuth();
  const canAmend = holds(permissions, 'lab.result-amend')
    && (test.valueType === 'NUMERIC' || test.valueType === 'TEXT');

  const released = !!test.result?.releasedAt;
  const enterable = test.valueType !== 'ENUM' || test.answerOptions.length > 0;
  const save = async () => {
    if (value === '' || busy) return;
    setBusy(true);
    try { await onEnter(); } finally { setBusy(false); }
  };

  const critical = isCritical(test.result?.flag);
  const outside = !!test.result && test.result.flag !== 'NORMAL';

  return (
    <div className={cn('flex flex-wrap items-center gap-3 px-4 py-3', critical && 'bg-danger-100/50', !test.result && test.sentBack && 'bg-warning-100/40')}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('text-body text-neutral-900', outside && 'font-semibold')}>{test.name}</span>
          {test.isMandatory && <StatusBadge tone="neutral">Required</StatusBadge>}
          {!test.result && test.sentBack && <StatusBadge tone="warning">Sent back — re-enter</StatusBadge>}
        </div>
        <div className="flex flex-wrap items-center gap-2 font-mono text-caption font-normal text-neutral-500">
          <span>{test.code}</span>
          {test.sample && <span>· {test.sample.barcode}</span>}
          {/* The band the value is about to be flagged against, shown BEFORE
              it is typed. A technician learning the range from the flag
              afterwards has already recorded the number. */}
          {bandLabel(test.referenceRange) && (
            <span className="font-sans text-neutral-500">
              ref {bandLabel(test.referenceRange)}{test.unit ? ` ${test.unit}` : ''}
            </span>
          )}
        </div>
      </div>

      {test.result && (
        <div className="flex items-center gap-2">
          <span className={cn('tabular-nums text-neutral-900', outside ? 'text-body-lg font-semibold' : 'text-body')}>
            {test.result.valueNumeric
              ?? (test.result.valueText != null ? readableValue(test.result.valueText) : null)
              ?? (test.result.valueBoolean == null
                ? null
                : test.result.valueBoolean ? 'Yes' : 'No')}
            {test.result.unit ? ` ${test.result.unit}` : ''}
          </span>
          <StatusBadge tone={flagTone(test.result.flag)}>
            {critical && <Siren aria-hidden className="h-3.5 w-3.5" />}
            {flagWord(test.result.flag, test.valueType)}
          </StatusBadge>
          <StatusBadge tone={released ? 'success' : test.result.validatedAt ? 'info' : 'neutral'}>
            {released ? 'Released' : test.result.validatedAt ? 'Validated' : 'Entered'}
          </StatusBadge>
        </div>
      )}

      {released ? (
        // A released result is immutable. Changing it means an amendment,
        // which creates a new attempt and leaves the original standing.
        canAmend
          ? <RowActions><IconAction label={`Amend ${test.name}`} icon={Pencil} onClick={() => setAmending(true)} /></RowActions>
          : (
            <span className="inline-flex items-center gap-1 text-caption font-normal text-neutral-500">
              <Lock aria-hidden className="h-3.5 w-3.5 text-neutral-400" /> Released — final
            </span>
          )
      ) : !canEnter ? (
        !test.result && <span className="text-caption font-normal text-neutral-500">Not entered yet</span>
      ) : !atBench ? (
        <span className="text-caption font-normal text-neutral-500">Tube not received yet</span>
      ) : (
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <ValueInput
            test={test}
            value={value}
            onChange={onChange}
            // A first value: Enter moves on, saving is one checked step at
            // the foot. A re-entry is a single value, checked on its own.
            onSubmit={() => (test.result ? void save() : onNext())}
            onBlur={onBlur}
            onRefused={() => setRefused(true)}
            invalid={!!error}
            disabled={busy || disabled}
          />
          {test.valueType === 'NUMERIC' && test.unit && (
            <span className="text-caption font-normal text-neutral-500">{test.unit}</span>
          )}
          {enterable && test.result && (
            <Button size="sm" variant="ghost" loading={busy} disabled={value === '' || disabled} onClick={() => void save()}>
              Re-enter
            </Button>
          )}
        </div>
      )}

      {error && <p role="alert" className="w-full text-caption text-danger-600">{error}</p>}
      {/* What the typed number will be flagged as, said before it is saved. */}
      {preview && !error && (
        <p className="flex w-full items-center gap-2 text-caption text-neutral-600" aria-live="polite">
          Will be flagged:
          <StatusBadge tone={flagTone(preview)}>
            {preview.startsWith('CRITICAL') && <Siren aria-hidden className="h-3.5 w-3.5" />}
            {flagWord(preview, test.valueType)}
          </StatusBadge>
        </p>
      )}
      {!error && refused && (
        <p className="w-full text-caption text-neutral-600">Digits and one decimal point only — use a dot, not a comma (13.2).</p>
      )}
      {!error && slip && (
        <p className="w-full text-caption font-medium text-warning-600">
          {value}{test.unit ? ` ${test.unit}` : ''} is ten times off the normal range — check the decimal point against the printout.
        </p>
      )}

      {/* Why the box is empty again: the pathologist did not accept what was
          typed. Said here, at the test, because this is where it is redone. */}
      {!test.result && test.sentBack && (
        <p className="w-full rounded-input border border-warning-600/25 bg-warning-100 px-3 py-2 text-caption text-warning-600">
          Sent back by the pathologist
          {test.sentBack.value != null ? ` (was ${readableValue(test.sentBack.value)}${test.unit ? ` ${test.unit}` : ''})` : ''}
          {/* The reason is typed by a person and rarely ends in a full stop;
              without one it ran straight into the next sentence. */}
          {test.sentBack.reason ? `: “${test.sentBack.reason.trim().replace(/[.\s]+$/, '')}”.` : '.'} Enter it again from the analyser printout.
        </p>
      )}

      {amending && test.result && (
        <AmendDialog test={test} onClose={() => setAmending(false)} onDone={() => { setAmending(false); onAmended(); }} />
      )}
    </div>
  );
}

/**
 * Amending a released value. The original is never overwritten: a new attempt
 * is written with the reason, goes back through validation and release, and
 * anything already reported on the old value stands as history.
 */
function AmendDialog({ test, onClose, onDone }: {
  test: LabBatteryTest; onClose: () => void; onDone: () => void;
}) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const numeric = test.valueType === 'NUMERIC';

  async function go() {
    setBusy(true); setError(null);
    try {
      let corrected: number | undefined;
      if (numeric) {
        const m = parseMeasured(value, { unit: test.unit, plausible: test.plausibleRange });
        if (!m.ok) { setError(m.message); setBusy(false); return; }
        corrected = m.value;
      }
      await api.post(`/lab/results/${test.result!.id}/amend`, {
        ...(numeric ? { valueNumeric: corrected } : { valueText: value.trim() }),
        reason: reason.trim(),
      }, crypto.randomUUID());
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Amend ${test.name}`} onClose={onClose}>
      <p className="mt-1 text-small text-neutral-600">
        Released as {String(test.result!.valueNumeric ?? test.result!.valueText ?? '—')}{test.result!.unit ? ` ${test.result!.unit}` : ''}.
        The new value is a fresh attempt and must be validated and released again before anyone sees it.
      </p>
      <TextField label={`Corrected value${test.unit ? ` (${test.unit})` : ''}`} value={value} onChange={setValue} required autoFocus
        inputMode={numeric ? 'decimal' : 'text'} />
      <TextField label="Why" value={reason} onChange={setReason} required
        hint="At least ten characters. Kept on both attempts."
        placeholder="Transcription error — analyser printout reads 11.2" />
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={go} loading={busy} disabled={reason.trim().length < 10 || value.trim() === ''}>
          Issue amendment
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * The last look before values are saved: every typed value with the flag it
 * will carry, critical ones first. Each critical value is confirmed against
 * the analyser printout before Save opens — a slipped decimal point is caught
 * here, while it costs nothing, instead of by the pathologist afterwards.
 */
function ReviewDialog({ items, subject, busy, onBack, onConfirm }: {
  items: Pending[]; subject: string; busy: boolean; onBack: () => void; onConfirm: () => void;
}) {
  const ordered = [...items].sort((a, b) =>
    (SEVERITY[a.flag ?? 'NORMAL'] ?? 2) - (SEVERITY[b.flag ?? 'NORMAL'] ?? 2));
  const critical = ordered.filter((p) => p.flag?.startsWith('CRITICAL'));
  const outside = ordered.filter((p) => p.flag === 'HIGH' || p.flag === 'LOW').length;
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const ready = critical.every((p) => ticked.has(p.t.id));
  const show = (p: Pending) => {
    if (p.t.valueType === 'ENUM') return p.t.answerOptions.find((o) => o.code === p.raw)?.display ?? p.raw;
    if (p.t.valueType === 'BOOLEAN') return p.raw === 'true' ? 'Yes' : 'No';
    return `${p.raw}${p.t.valueType === 'NUMERIC' && p.t.unit ? ` ${p.t.unit}` : ''}`;
  };
  return (
    <Dialog
      title={`Check ${items.length} value${items.length === 1 ? '' : 's'} before saving`}
      onClose={busy ? () => undefined : onBack}
      footer={(
        <>
          <Button variant="secondary" onClick={onBack} disabled={busy}>Back to edit</Button>
          <Button loading={busy} disabled={!ready} onClick={onConfirm}>
            Save {items.length} value{items.length === 1 ? '' : 's'}
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        <p className="text-small text-neutral-700">
          <span className="font-mono font-medium text-neutral-900">{subject}</span>.
          {' '}{critical.length
            ? `${critical.length} critical value${critical.length === 1 ? '' : 's'} — tick each one after re-reading it on the analyser printout.`
            : outside ? `${outside} outside the normal range. Read them against the printout once more.`
              : 'All within the normal range.'}
        </p>
        <ul className="divide-y divide-neutral-100 rounded-card border border-neutral-200">
          {ordered.map((p) => {
            const crit = p.flag?.startsWith('CRITICAL');
            return (
              <li key={p.t.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2', crit && 'bg-danger-100')}>
                <span className="min-w-0 flex-1 text-small text-neutral-900">{p.t.name}</span>
                <span className={cn('tabular-nums text-body', p.flag && p.flag !== 'NORMAL' ? 'font-semibold' : '')}>{show(p)}</span>
                {p.flag && (
                  <StatusBadge tone={flagTone(p.flag)}>
                    {crit && <Siren aria-hidden className="h-3.5 w-3.5" />}{flagWord(p.flag, p.t.valueType)}
                  </StatusBadge>
                )}
                {crit && (
                  <label className="flex w-full items-center gap-2 text-caption font-medium text-danger-600 max-md:min-h-10">
                    <input type="checkbox" className="h-4 w-4 accent-danger-600" checked={ticked.has(p.t.id)}
                           onChange={() => setTicked((s) => { const n = new Set(s); if (n.has(p.t.id)) n.delete(p.t.id); else n.add(p.t.id); return n; })} />
                    I re-read {show(p)} on the analyser printout — it is correct
                  </label>
                )}
              </li>
            );
          })}
        </ul>
        <p className="text-caption text-neutral-500">Saved values go to the pathologist to validate and release. Each is entered against its own tube.</p>
      </div>
    </Dialog>
  );
}
